import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormControl } from '@angular/forms';
import { UserService } from '../../../core/user/user.service';
import { SubscriptionLayout, SubscriptionService } from '../../../core/subscription/subscription.service';
import { SubscriptionInterval, SubscriptionPlanName, SubscriptionType } from '../../../core/subscription/subscription';
import { Breakpoint, fromMediaQuery } from '../../../core/helpers/utils';
import { SessionService } from '../../../core/session.service';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Router } from '@angular/router';

@Component({
  selector: 'rosie-subscription-plans-checkout',
  templateUrl: './subscription-plans-checkout.component.html',
  styleUrls: ['./subscription-plans-checkout.component.scss'],
  standalone: false
})
export class SubscriptionPlansCheckoutComponent implements OnInit {
  @Input() showIntervalSelectionCopy = false;
  @Input() showSelectedPlan: boolean = false;
  @Input() showWebsiteTextingAddon: boolean = false;
  @Input() isOnboardingLayout: boolean = false;
  @Input() hideIntervalToggle = false;
  // Lowercase plan key currently being changed via Shopify provider billing (set
  // by the parent while the change request is in flight). Locks the plan buttons
  // and spins the one being changed. Only meaningful for Shopify billing.
  @Input() changingPlanKey: string | null = null;
  // Lowercase plan key (e.g. 'scale') to crown with the "Recommended for you"
  // banner (ENG-2292). When set, it replaces the "Most Popular" ribbon and
  // the highlighted button moves to that plan.
  @Input() recommendedPlan: string | null = null;
  @Output() providerPlanSelected = new EventEmitter<string>();
  // Fires (Stripe billing only) right before the subscription modal opens for
  // a plan card click, so hosts can track the add-card intent.
  @Output() planSelected = new EventEmitter<string>();

  websiteTextingControl = new UntypedFormControl(false);
  errorMessage = '';
  billingPortalUrl: SafeResourceUrl;
  isMobileVersion = false;
  planOptionsDisplay: any[] = [];
  SubscriptionInterval = SubscriptionInterval;
  // Every stream this component listens to is tied to its lifetime. The
  // checkout is instantiated several times per session (Go Live, Billing, the
  // mobile/desktop branches of the subscription modal), and constructor-time
  // subscriptions without teardown kept every destroyed instance alive; each
  // business switch then fired one GET subscription-plans per leaked instance
  // (8 parallel requests on dev, tripping Stripe's test-mode rate limit).
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private user: UserService,
    private translate: TranslateService,
    public subscription: SubscriptionService,
    private session: SessionService,
    private modal: NgxSmartModalService,
    private router: Router
  ) {
    this.planOptionsDisplay = this.buildPlanOptions();
  }

  get isCouponRevalidating(): boolean {
    return this.subscription.isRevalidatingCoupon;
  }

  get hasCreditCardOnFile(): boolean {
    return this.user.hasCreditCardOnFile;
  }

  get isPaymentMethodInvalid(): boolean {
    return this.subscription.isPaymentMethodInvalid;
  }

  get upgradeToPlanActive(): SubscriptionPlanName {
    return this.subscription.upgradeToPlanActive;
  }

  get planOptions() {
    return this.subscription.planOptions;
  }

  get isExpanded(): boolean {
    return this.subscription.isExpanded;
  }

  get selectedInterval(): SubscriptionInterval {
    return this.subscription.selectedInterval;
  }

  set selectedInterval(value: SubscriptionInterval) {
    this.subscription.selectedInterval = value;
  }

  set isExpanded(value: boolean) {
    this.subscription.isExpanded = value;
  }

  get hideChangePlan(): boolean {
    return document.location?.href?.includes('settings');
  }

  get selectedPlanDisplay() {
    return this.subscription.selectedPlanDisplay;
  }

  get previewedPlan() {
    const previewPlan = this.selectedPlan?.name || this.initialPlan;
    const planDetails = this.planOptions.find((plan) => plan.value === previewPlan);
    return planDetails;
  }

  isRecommendedPlan(plan): boolean {
    return !!this.recommendedPlan && plan?.value?.toLowerCase() === this.recommendedPlan.toLowerCase();
  }

  // Which card gets the accent: the recommended plan when one is provided,
  // otherwise Scale ("Most Popular").
  isHighlightedPlan(plan): boolean {
    return this.recommendedPlan ? this.isRecommendedPlan(plan) : plan?.value === 'Scale';
  }

  isPlanSelected(plan): boolean {
    const currentPlan = this.subscription.initialPlan;
    const currentInterval = this.subscription.initialInterval;
    return currentPlan === plan?.value && currentInterval === this.selectedInterval;
  }

  get initialPlan(): string {
    return this.subscription.initialPlan;
  }

  get selectedPlan() {
    return this.subscription.selectedPlan;
  }

  get isTrialing(): boolean {
    return this.subscription.isTrialing;
  }

  get isUpgrade() {
    return this.subscription.isUpgrade;
  }

  get isEnterprisePlanSelected(): boolean {
    return this.subscription.isEnterprisePlanSelected;
  }

  get isPlanChangeInProgress(): SubscriptionPlanName | boolean {
    return this.subscription.isPlanChangeInProgress;
  }

  get hasUpgradeToPlanActive() {
    return this.subscription.upgradeToPlanActive;
  }

  ngOnInit() {
    this.session.refreshState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.subscription.getPlans().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.subscription.plans) {
          this.planOptionsDisplay = this.buildPlanOptions();
        }
      });
    });

    this.subscription.couponChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((couponCode: string) => {
      if (!couponCode && this.allPlansHaveCoupon()) {
        this.resetPlans();
      } else if (!couponCode) {
        this.subscription.selectedPlanDisplay = null;
        this.updateSelectedPlanDisplay();
      } else {
        this.updateSelectedPlanDisplay();
      }
    });

    this.subscription.created.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resetPlans());

    this.subscription.selectedPlanControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((plan) => {
      if (!this.selectedPlanDisplay || this.selectedPlanDisplay?.value !== plan?.value) {
        this.updateSelectedPlanDisplay();
      }
    });

    this.subscription.websiteTextingPreSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.websiteTextingControl.setValue(value, { emitEvent: false });
    });

    fromMediaQuery(Breakpoint.UpToLarge).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(matches => {
      this.isMobileVersion = matches;
      if (!this.upgradeToPlanActive || this.hasCreditCardOnFile) {
        this.isExpanded = false;
      }
    });

    this.subscription.reset.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.subscription.layout = null;
      this.resetToPreviousPlan();
    });

    if (!this.planOptionsDisplay?.length) {
      this.subscription.getPlans().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.subscription.plans?.length) {
          this.planOptionsDisplay = this.buildPlanOptions();
        }
      });
    }
  }

  togglePlans() {
    this.isExpanded = !this.isExpanded;
  }

  getEffectiveInitialPlan(): SubscriptionPlanName {
    return this.subscription.initialPlan;
  }

  get isUserOnBillingV4(): boolean {
    return this.user.isUserOnBillingV4;
  }

  get isOnLegacyUnlimitedMinutes(): boolean {
    return !this.user.isUserOnBillingV4;
  }

  get trialDaysLeft(): number {
    return this.user.subscription?.trialDaysLeft;
  }

  onWebsiteTextingToggle(): void {
    const value = this.websiteTextingControl.value;
    this.subscription.websiteTextingPreSelected$.next(value);
  }

  buildPlanOptions(interval?: SubscriptionInterval, skipSelection: boolean = false): any[] {
    if (!this.subscription.plans || !Array.isArray(this.subscription.plans)) {
      return [];
    }
    const resolvedInterval = interval || this.subscription.initialInterval;

    const plans = this.subscription.plans.map((plan) => {
      // Pricing-grid bullet copy comes from the API's additive `display_features`
      // field (config/pricing_features.php), curated per plan to mirror the
      // marketing grid. Kept separate from `features` so the mobile-facing
      // features[] contract is untouched.
      const filteredFeatures = plan?.displayFeatures || [];

      const originalPrice = this.getOriginalPrice(plan, resolvedInterval);
      const discountedPrice = this.getDiscountedPrice(plan, resolvedInterval);
      const hasDiscount = discountedPrice !== null;
      const showOriginalPrice = hasDiscount && resolvedInterval === SubscriptionInterval.Monthly;

      // For monthly: use discounted price directly
      // For annual: use getDisplayPrice which divides annual price by 12
      let displayPrice;
      if (hasDiscount && resolvedInterval === SubscriptionInterval.Monthly) {
        displayPrice = discountedPrice;
      } else {
        displayPrice = this.getDisplayPrice(plan, resolvedInterval);
      }

      // Format price with decimals if needed
      const formattedPrice = displayPrice % 1 === 0 ? displayPrice.toString() : displayPrice.toFixed(2);

      return {
        value: this.getPlanName(plan),
        label: `${plan?.name}`,
        price: `$${formattedPrice}`,
        originalPrice: showOriginalPrice ? `$${originalPrice}` : null,
        fullMonthlyPrice: `$${this.getOriginalPrice(plan, SubscriptionInterval.Monthly)}`,
        annualBillingAmount: this.getAnnualBillingAmount(plan),
        annualSavings: this.getAnnualSavings(plan),
        isYearly: resolvedInterval === SubscriptionInterval.Yearly,
        couponPreview: plan?.couponPreview,
        features: filteredFeatures?.map((feature) => ({
          text: feature.replace('(Beta)', ''),
          isHiddenOnMobileCollapsed: false,
        })),
        isInitialSelection: plan?.name === this.getEffectiveInitialPlan(),
        hasPremiumFeatures: plan?.name === SubscriptionPlanName.Growth || plan?.name === SubscriptionPlanName.Scale,
        // Shopify-only display copy from the API (null for Stripe plans).
        highlight: plan?.highlight || null,
        inheritsLabel: plan?.inheritsLabel || null,
      }
    });


    if (plans.length > 0 && !skipSelection) {
      const initialPlan = plans.find((plan) => plan.isInitialSelection);
      this.selectPlan(initialPlan, true);
      this.selectedInterval = plans.find((plan) => plan.isInitialSelection)?.isYearly ? SubscriptionInterval.Yearly : SubscriptionInterval.Monthly;
    }

    this.planOptionsDisplay = plans;
    this.subscription.planOptions = plans;

    return plans;
  }

  getMinutesIncluded(plan?: any): number {
    return plan?.minutesIncluded || 0;
  }

  getOriginalPrice(plan?: any, interval?: SubscriptionInterval): number {
    const price = this.getLicensedPlanPrice(plan, interval);
    return price?.amount || 0;
  }

  getDiscountedPrice(plan?: any, interval?: SubscriptionInterval): number | null {
    if (!plan?.couponPreview?.prices) {
      return null;
    }

    const licensedPrice = this.getLicensedPlanPrice(plan, interval);
    if (!licensedPrice) {
      return null;
    }

    const targetInterval = interval === SubscriptionInterval.Yearly ? 'year' : 'month';
    const discountedPrice = plan.couponPreview.prices.find(
      (cp) => cp.priceId === licensedPrice.id && cp.interval === targetInterval
    );

    return discountedPrice ? discountedPrice.discountedAmount : null;
  }

  getAppliedCoupon(): any {
    if (!this.subscription.plans) {
      return null;
    }

    const planWithCoupon = this.subscription.plans.find(p => p.couponPreview);
    return planWithCoupon?.couponPreview?.coupon || null;
  }

  allPlansHaveCoupon(): boolean {
    if (!Array.isArray(this.subscription.plans) || this.subscription.plans.length === 0) {
      return false;
    }

    return this.subscription.plans.every(plan => plan.couponPreview);
  }

  getCouponDescription(): string {
    const coupon = this.getAppliedCoupon();
    if (!coupon) {
      return '';
    }

    let discountText = '';
    if (coupon.percentOff) {
      discountText = `${coupon.percentOff}% off`;
    } else if (coupon.amountOff) {
      discountText = `$${coupon.amountOff / 100} off`;
    }

    if (coupon.duration === 'repeating' && coupon.durationInMonths) {
      return `${discountText} for ${coupon.durationInMonths} ${coupon.durationInMonths === 1 ? 'month' : 'months'}.`;
    } else if (coupon.duration === 'once') {
      return `${discountText} for 1 month.`;
    } else if (coupon.duration === 'forever') {
      return `${discountText} forever.`;
    }

    return discountText;
  }

  removeCoupon(): void {
    this.subscription.couponChanged.next('');
  }

  private resetPlans(): void {
    this.subscription.appliedCoupon = null;

    // Clear the cached selectedPlanDisplay to prevent stale coupon data
    this.subscription.selectedPlanDisplay = null;

    if (this.subscription.plans) {
      this.subscription.plans = this.subscription.plans.map(plan => ({
        ...plan,
        couponPreview: undefined
      }));
    }

    this.planOptionsDisplay = this.buildPlanOptions(this.selectedInterval, true);
    this.subscription.planOptions = this.planOptionsDisplay;
    this.updateSelectedPlanDisplay();
  }

  getLicensedPlanPrice(plan?: any, interval?: SubscriptionInterval): any {
    const targetInterval = interval || SubscriptionInterval.Monthly;

    if (!plan?.prices || !Array.isArray(plan.prices)) {
      return null;
    }

    const foundPrice = plan.prices.find((price) =>
      price.usageType === SubscriptionType.Licensed &&
      price.interval === targetInterval
    );

    if (foundPrice) {
      return foundPrice;
    }

    if (targetInterval === SubscriptionInterval.Yearly) {
      const monthlyPrice = plan.prices.find((price) =>
        price.usageType === SubscriptionType.Licensed &&
        price.interval === SubscriptionInterval.Monthly
      );

      if (monthlyPrice) {
        return {
          ...monthlyPrice,
          interval: SubscriptionInterval.Yearly,
          amount: Math.round(monthlyPrice.amount * 10)
        };
      }
    }

    return null;
  }

  getDisplayPrice(plan?: any, interval?: SubscriptionInterval): number {
    const targetInterval = interval || SubscriptionInterval.Monthly;

    if (targetInterval === SubscriptionInterval.Yearly) {
      // Check if there's a discounted annual price from coupon
      const discountedYearlyPrice = this.getDiscountedPrice(plan, SubscriptionInterval.Yearly);
      if (discountedYearlyPrice !== null) {
        // With discount: show actual price with decimals
        return Math.round((discountedYearlyPrice / 12) * 100) / 100;
      }

      const monthlyPrice = plan?.prices?.find((price) =>
        price.usageType === SubscriptionType.Licensed &&
        price.interval === SubscriptionInterval.Monthly
      );

      if (monthlyPrice) {
        // Without discount: round to nearest whole number
        return Math.round(monthlyPrice.amount * 0.8333);
      }
    }

    const price = this.getLicensedPlanPrice(plan, interval);
    return price?.amount || 0;
  }

  getAnnualBillingAmount(plan?: any): string {
    // Check if there's a discounted annual price from coupon
    const discountedYearlyPrice = this.getDiscountedPrice(plan, SubscriptionInterval.Yearly);
    if (discountedYearlyPrice !== null) {
      return discountedYearlyPrice.toLocaleString();
    }

    const monthlyPrice = plan?.prices?.find((price) =>
      price.usageType === SubscriptionType.Licensed &&
      price.interval === SubscriptionInterval.Monthly
    );

    const amount = monthlyPrice ? monthlyPrice.amount * 10 : 0;
    return amount.toLocaleString();
  }

  /**
   * Annual savings for the "Save $X" badge: full monthly cost over 12 months
   * minus what they'd actually pay annually (monthly × 10 under the standard
   * "2 months free" model, or the coupon-discounted annual price). Returns a
   * whole-dollar number; falls back to 0 when pricing data is missing.
   */
  getAnnualSavings(plan?: any): number {
    const monthly = this.getOriginalPrice(plan, SubscriptionInterval.Monthly);
    if (!monthly) {
      return 0;
    }

    const discountedYearly = this.getDiscountedPrice(plan, SubscriptionInterval.Yearly);
    const annualTotal = discountedYearly !== null ? discountedYearly : monthly * 10;
    return Math.max(0, Math.round(monthly * 12 - annualTotal));
  }

  getPlanName(plan?: any): string {
    return plan?.name || '';
  }

  trackByPlan(index: number, plan: any): string {
    return plan.value;
  }

  trackByFeature(index: number, feature: any): string {
    return feature.text;
  }

  selectPlan(plan: any, disablePreselect?: boolean, isManualSelection?: boolean): void {
    this.subscription.selectPlan(plan, disablePreselect, isManualSelection);
    this.updateSelectedPlanDisplay();
  }

  updateSelectedPlanDisplay(): void {
    const selectedPlanDisplay = this.buildSelectedPlanDisplay();
    if (selectedPlanDisplay) {
      this.subscription.selectedPlanDisplay = selectedPlanDisplay;
    }
  }

  // Spinner shows on the specific Shopify plan whose change is in flight.
  isPlanChanging(plan: any): boolean {
    return this.subscription.isShopifyBillingProvider
      && !!this.changingPlanKey
      && this.changingPlanKey === plan.value?.toLowerCase();
  }

  // While any Shopify plan change is in flight, lock all the plan buttons.
  get isAnyPlanChanging(): boolean {
    return this.subscription.isShopifyBillingProvider && !!this.changingPlanKey;
  }

  handleSelectPlanClick(plan: any) {
    if (this.subscription.isShopifyBillingProvider) {
      if (this.changingPlanKey) {
        return;
      }
      this.providerPlanSelected.emit(plan.value?.toLowerCase());
      return;
    }

    this.subscription.selectedInterval = this.selectedInterval;
    this.selectPlan(plan, true, true);
    this.planSelected.emit(plan.value);
    this.subscription.open.next({ origin: SubscriptionLayout.SinglePlanLayout, plan: plan.value });
  }

  private buildSelectedPlanDisplay(): any {
    const plan = this.planOptionsDisplay.find((p) => p.value === this.selectedPlan?.name);
    if (!plan) {
      return null;
    }

    // Check if service has set selectedPlanDisplay with coupon data for trialing users, downgrades, or upgrades
    const servicePlanDisplay = this.subscription.selectedPlanDisplay;
    const useServiceDisplay = (this.isTrialing || this.subscription.isDowngrade || this.subscription.isUpgrade) &&
      servicePlanDisplay?.couponPreview &&
      servicePlanDisplay?.name === this.selectedPlan?.name;

    // Use the plan with coupon data if available during trial, downgrade, or upgrade, otherwise use selectedPlan
    const planData = useServiceDisplay ? servicePlanDisplay : this.selectedPlan;

    const originalPrice = this.getOriginalPrice(planData, this.selectedInterval);
    const discountedPrice = this.getDiscountedPrice(planData, this.selectedInterval);
    const hasDiscount = discountedPrice !== null;
    const showOriginalPrice = hasDiscount && this.selectedInterval === SubscriptionInterval.Monthly;

    let displayPrice;
    if (hasDiscount && this.selectedInterval === SubscriptionInterval.Monthly) {
      displayPrice = discountedPrice;
    } else {
      displayPrice = this.getDisplayPrice(planData, this.selectedInterval);
    }

    // Format price with decimals if needed
    const formattedPrice = displayPrice % 1 === 0 ? displayPrice.toString() : displayPrice.toFixed(2);

    // Build the result, explicitly controlling couponPreview
    const { couponPreview: _, ...planWithoutCoupon } = plan;
    return {
      ...planWithoutCoupon,
      price: `$${formattedPrice}`,
      originalPrice: showOriginalPrice ? `$${originalPrice}` : null,
      fullMonthlyPrice: `$${this.getOriginalPrice(planData, SubscriptionInterval.Monthly)}`,
      annualBillingAmount: this.getAnnualBillingAmount(planData),
      annualSavings: this.getAnnualSavings(planData),
      isYearly: this.selectedInterval === SubscriptionInterval.Yearly,
      // Only include couponPreview if we're using service display and it has coupon data
      ...(useServiceDisplay && planData?.couponPreview ? {
        couponPreview: planData.couponPreview,
        prices: planData.prices,
        name: planData.name
      } : {})
    };
  }

  getSelectPlanButtonTranslationKey(plan: any): string {
    const intervalSuffix = this.showIntervalSelectionCopy ? (this.selectedInterval === SubscriptionInterval.Yearly ? ' Annually' : ' Monthly') : ' Plan';
    // Check if this is the user's actual current plan (not just selected in modal)
    const isActualCurrentPlan = this.subscription.initialPlan === plan?.value && this.subscription.initialInterval === this.selectedInterval;
    // Full plan name on the button ("Select Professional Plan"), per the ENG-2292 pricing grid.
    return isActualCurrentPlan && this.hasCreditCardOnFile ? `Current Plan` : `Select ${plan?.value}${intervalSuffix}`;
  }

  getPlanDescriptionTranslationKey(plan: any): string {
    return `ACCOUNT.BILLING.${plan?.value?.toUpperCase()}_DESCRIPTION_V3`;
  }

  getCustomClassForPlan(plan: any): string {
    const isActualCurrentPlan = this.subscription.initialPlan === plan?.value && this.subscription.initialInterval === this.selectedInterval;
    return (isActualCurrentPlan && this.hasCreditCardOnFile) ? 'plan-selection selected' : (this.isHighlightedPlan(plan) && !this.hasCreditCardOnFile ? 'plan-selection highlighted' : 'plan-selection');
  }

  changePlan() {
    if (document.location?.href?.includes('settings')) {
      this.router.navigate(['/account', { section: 'billing' }]);
    } else if (this.user.hasTrialExpired) {
      // Trial-extension modal: send the user back to the onboarding plan
      // selector (Go-Live step). hasTrialExpired guarantees no card on file,
      // so the launch step renders the plan grid rather than launch instructions.
      this.router.navigate(['/setup', { step: 'launch' }]);
    }
    this.modal.closeAll();
  }

  selectInterval(interval: SubscriptionInterval) {
    this.selectedInterval = interval;
    this.planOptionsDisplay = this.buildPlanOptions(this.selectedInterval);
    this.updateSelectedPlanDisplay();
  }

  get intervalText(): string {
    return this.selectedInterval === SubscriptionInterval.Yearly ? 'per month' : 'per month';
  }

  get billingIntervalText(): string {
    return this.selectedInterval === SubscriptionInterval.Yearly ? '/year' : '/month';
  }

  private resetToPreviousPlan(): void {
    this.subscription.resetToPreviousPlan();
  }
}
